import { Card, TextField, Button } from "@mui/material"
import FilterListIcon from "@mui/icons-material/FilterList"
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import MDBox from "components/MDBox"
import { useState, useEffect } from "react"
import moment from "moment"
import FilterClassSituation from "components/FilterClassSituation/FilterClassSituation"
import ColumnTreeMenu from "./ColumnTreeMenu"
import {
  ALL_COLUMN_KEYS,
  DEFAULT_HIDDEN_COLUMN_KEYS,
  LOCKED_COLUMN_KEYS,
} from "./classSituationColumnTree"
import { Loading } from "shared/components/ui/Loading"
import TableSituation from "pages/assistant/components/TableSituation/TableSituation"

import { useLazyGetClassSituationSelectionCXOQuery } from "shared/redux/endpoints/lxo"

import {
  useLazyGetClassSituationLXOQuery,
  useLazyGetClassSituationLXOCountQuery,
  useLazyGetClassSituationLXONumberBlockQuery,
  useCreateClassSituationNoteLXOMutation,
} from "shared/redux/endpoints/lxo/classSituation"
import { selectAuth } from "shared/redux/slices/authSlice"
import { useSelector } from "react-redux"
import MDSelect from "components/MDSelect"
import {
  mapClassSituationList,
  normalizePagination,
  mapNumberBlockResponse,
  mapClassSituationSummary,
  buildClassSituationSummaryItems,
} from "./classSituationMappers"
import { exportClassSituationCsv } from "./classSituationExport"
import { sortClassSituationRows } from "./classSituationSort"

const ALL_CLASSES_PAGE_SIZE = 10000
const HIDDEN_COLUMNS_STORAGE_KEY = "classSituation.hiddenColumns.v2"

const ACS_STATUS_OPTIONS = [
  { value: null, label: "All" },
  { value: "Low ACS", label: "Low on ACS" },
  { value: "Critical Low ACS", label: "Very Low on ACS" },
  { value: "Full", label: "Full on ACS" },
]

const defaultDateRange = () => ({
  fromDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
  toDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
})

function ClassSituationLXO() {
  // redux
  const auth = useSelector(selectAuth)

  const centerId = auth?.centerId
  // state
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const [state, setState] = useState({
    page: 0,
    size: ALL_CLASSES_PAGE_SIZE,
    centerId: auth?.centerId,
    search: "",
    ...defaultDateRange(),
  })
  const [searchInput, setSearchInput] = useState("")

  const [filterData, setFilterData] = useState({
    search: null,
    koc: null,
    schedule: null,
    classLevel: null,
    teacher: null,
    status: null,
    acsStatus: null,
    cxo: null,
    ...defaultDateRange(),
  })

  const [pagination, setPagination] = useState({
    totalPage: 0,
    currentPage: 0,
    totalElements: 0,
  })

  const [createNote] = useCreateClassSituationNoteLXOMutation()
  const [triggerClassSituationList] = useLazyGetClassSituationLXOQuery()
  const [triggerClassSituationCount] = useLazyGetClassSituationLXOCountQuery()
  const [triggerClassSituationNumberBlock] = useLazyGetClassSituationLXONumberBlockQuery()
  const [triggerCXOSelection] = useLazyGetClassSituationSelectionCXOQuery()

  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [columnAnchorEl, setColumnAnchorEl] = useState(null)
  const [statusOptions, setStatusOptions] = useState([])
  const [statusFilter, setStatusFilter] = useState(null)
  const [acsStatusFilter, setAcsStatusFilter] = useState(null)
  const [ecOptions, setEcOptions] = useState([])
  const [selectedEc, setSelectedEc] = useState(null)
  const [expandedCategories, setExpandedCategories] = useState({})

  const PREVIEW_CARDS_PER_CATEGORY = 3
  const [summary, setSummary] = useState({
    stsNo: null,
    noClass: 0,
    acs: 0,
    lowAcs: 0,
    criticalLowAcs: 0,
    overAcs: 0,
    acsValue: null,
    acsLastWeek: null,
    acsThisWeek: null,
    acsNextWeek: null,
    acsFinalWeekOfMonth: null,
    isLowAcs: null,
    isVeryLowAcs: null,
    studentOutstanding: 0,
    absentOver2Consecutive: null,
    totalTeachingHr: 0,
    attendanceLastWeek: null,
    totalStsVsLastWeek: null,
    totalStudentsNextWeek: null,
    eppThisWeekMonthNext2Months: 0,
    eppThisMonth: 0,
    eppNextMonth: 0,
    eppNext2months: 0,
    waitingToEnroll: 0,
    waitingToClass: null,
    stsDelay: 0,
    stsJoin1stLesson: 0,
    firstLesson: 0,
    unitTest: 0,
    eocc: 0,
    parentsNotContacted6Weeks: null,
  })

  const [dataTable, setDataTable] = useState({
    rows: [],
  })
  const [tableSummary, setTableSummary] = useState(null)

  // Persist the hidden list (not the visible one) so columns added later still show by default.
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let hidden = DEFAULT_HIDDEN_COLUMN_KEYS
    try {
      const saved = localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY)
      if (saved) hidden = JSON.parse(saved)
    } catch {
      // unreadable storage — fall back to defaults
    }
    return ALL_COLUMN_KEYS.filter((key) => LOCKED_COLUMN_KEYS.includes(key) || !hidden.includes(key))
  })

  const updateVisibleColumns = (next) => {
    setVisibleColumns(next)
    try {
      localStorage.setItem(
        HIDDEN_COLUMNS_STORAGE_KEY,
        JSON.stringify(ALL_COLUMN_KEYS.filter((key) => !next.includes(key)))
      )
    } catch {
      // storage unavailable (private mode) — keep in-memory only
    }
  }
  const filteredColumns = visibleColumns.map((accessor) => ({ accessor }))

  const showNumber = (v) =>
    v === null || v === undefined || v === "" ? "-" : v
  const showYesNo = (v) => (Number(v) > 0 ? "Yes" : "No")
  const yesNoTone = (v) => (Number(v) > 0 ? "red" : "green")

  const summaryGroups = [
    {
      category: "OVERALL",
      cards: [
        {
          title: "No. of Sts (last week / this week / next week)",
          value: `${summary.totalStsVsLastWeek ?? "-"} / ${summary.stsNo ?? "-"} / ${summary.totalStudentsNextWeek ?? "-"}`,
          tone: "green",
        },
        { title: "No. of Class", value: summary.noClass ?? "-", tone: "green" },
        { title: "Total Teaching Hours", value: summary.totalTeachingHr ?? "-", tone: "green" },
        { title: "Attendance Last Week", value: summary.attendanceLastWeek ?? "-", tone: "yellow" },
      ],
    },
    {
      category: "ACS",
      cards: [
        { title: "ACS", value: summary.acs ?? "-", tone: "yellow" },
        { title: "Low ACS", value: summary.lowAcs ?? "-", tone: "yellow", acsStatusValue: "Low ACS" },
        { title: "Critical Low ACS", value: summary.criticalLowAcs ?? "-", tone: "red", acsStatusValue: "Critical Low ACS" },
        { title: "ACS of Last Week", value: showNumber(summary.acsLastWeek), tone: "blue" },
        { title: "ACS of This Week", value: showNumber(summary.acsThisWeek), tone: "blue" },
        { title: "ACS of Next Week", value: showNumber(summary.acsNextWeek), tone: "blue" },
        // { title: "Low on ACS", value: showNumber(summary.isLowAcs), tone: yesNoTone(summary.isLowAcs) },
        // { title: "Very Low on ACS", value: showNumber(summary.isVeryLowAcs), tone: yesNoTone(summary.isVeryLowAcs) },
      ],
    },
    {
      category: "STUDENT SITUATION",
      cards: [
        { title: "No. of Outstanding", value: summary.studentOutstanding ?? "-", tone: "red" },
        { title: "No. of Absent (>2 consecutive lessons)", value: summary.absentOver2Consecutive ?? "-", tone: "red" },
        { title: "No. of EPP (this month / next month / next 2 months)", value: `${summary.eppThisMonth} / ${summary.eppNextMonth} / ${summary.eppNext2months}`, tone: "yellow" },
        { title: "No. of Waiting to Enroll", value: summary.waitingToEnroll ?? "-", tone: "yellow" },
        { title: "No. of Delay", value: summary.stsDelay ?? "-", tone: "red" },
      ],
    },
  ]
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }))
  }

  const normalizeECOption = (item) => {
    if (!item) return null
    if (typeof item === "string") return { value: item, label: item }

    const value = item.value ?? item.id ?? item.name ?? item.cxo ?? ""
    const label = item.label ?? item.name ?? item.cxo ?? String(value)

    if (!value) return null
    return { value: String(value), label: String(label) }
  }

  const loadECOptions = async (params) => {
    try {
      const resp = await triggerCXOSelection({
        page: 0,
        ...(params?.centerId && { centerId: params.centerId }),
      })
      const raw = resp?.data?.data ?? resp?.data ?? []
      const list = Array.isArray(raw) ? raw : []
      const mapped = list.map(normalizeECOption).filter(Boolean)

      setEcOptions([{ value: "", label: "All EC" }, ...mapped])
    } catch (error) {
      console.error("Failed to load EC options:", error)
      setEcOptions([{ value: "", label: "All EC" }])
    }
  }

  const handlerDataTable = (data, summary = null) => {
    setDataTable({
      totalElements: pagination.totalElements,
      rows: sortClassSituationRows(mapClassSituationList(data)),
    })
    setTableSummary(summary)
  }

  const handlerAPIList = async (params) => {
    setIsInitialLoading(true)

    try {
      const queryParams = {
        page: 0,
        size: params.size ?? ALL_CLASSES_PAGE_SIZE,
        ...(params.centerId && { centerId: params.centerId }),
        ...(params.search && { search: params.search }),
        ...(params.status && { status: params.status }),
        ...(params.acs_status && { acs_status: params.acs_status }),
        ...(params.cxo && { cxo: params.cxo }),
        ...(params.koc && { koc: params.koc }),
        ...(params.schedule && { schedule: params.schedule }),
        ...(params.classLevel && { classLevel: params.classLevel }),
        ...(params.teacher && { teacher: params.teacher }),
        ...(params.fromDate && { fromDate: params.fromDate }),
        ...(params.toDate && { toDate: params.toDate }),
      }

      const resp = await triggerClassSituationList(queryParams).unwrap()

      // Response shape: { status, data: { totalPage, totalElement, currentPage, data: [...] } }
      const dataBlock = resp?.data
      const items = dataBlock?.data ?? resp?.content ?? (Array.isArray(resp) ? resp : [])
      const paginationMeta = normalizePagination(dataBlock ?? resp, params.page ?? 0)
      const summary = mapClassSituationSummary(dataBlock?.summary)

      handlerDataTable(items, summary)
      setPagination(paginationMeta)

      return paginationMeta
    } catch (error) {
      console.error("Failed to fetch class situation list:", error)
      handlerDataTable([])
      setPagination({ totalPage: 0, currentPage: 0, totalElements: 0 })
      return { totalPage: 0, currentPage: 0, totalElements: 0 }
    } finally {
      setIsInitialLoading(false)
    }
  }

  const fetchSummary = async (params) => {
    try {
      const queryParams = {
        ...(params?.centerId && { centerId: params.centerId }),
        fromDate: params.fromDate,
        toDate: params.toDate,
      }
      const resp = await triggerClassSituationNumberBlock(queryParams).unwrap()
      const countData = resp?.data ?? resp
      const mapped = mapNumberBlockResponse(countData)
      if (mapped) setSummary(mapped)
    } catch (error) {
      console.error("Failed to fetch class situation count:", error)
    }
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setState((prev) => {
        const next = {
          ...prev,
          search: searchInput,
          page: 0,
        }
        handlerAPIList(next)
        return next
      })
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const handleFilterSubmit = (newFilters) => {
    const newFilterMap = {
      ...filterData,
      ...newFilters,
    }
    if (!newFilterMap.fromDate || !newFilterMap.toDate) {
      Object.assign(newFilterMap, defaultDateRange())
    }
    setFilterData(newFilterMap)
    if (
      newFilterMap.fromDate !== filterData.fromDate ||
      newFilterMap.toDate !== filterData.toDate
    ) {
      fetchSummary({ centerId, fromDate: newFilterMap.fromDate, toDate: newFilterMap.toDate })
    }
    const newState = {
      ...state,
      page: 0,
      koc: newFilterMap?.koc?.value ?? "",
      schedule: newFilterMap?.schedule?.value ?? "",
      classLevel: newFilterMap?.classLevel?.value ?? "",
      teacher: newFilterMap?.teacher?.value ?? "",
      status: newFilterMap?.status?.value ?? "",
      cxo: newFilterMap?.cxo?.value ?? "",
      fromDate: newFilterMap.fromDate,
      toDate: newFilterMap.toDate,
    }
    setSelectedEc(newFilterMap?.cxo ?? null)
    setState(newState)
    handlerAPIList(newState)
  }

  useEffect(() => {
    fetchSummary({ centerId, fromDate: filterData.fromDate, toDate: filterData.toDate })
    setStatusOptions([
      { value: null, label: "All" },
      { value: "Planning", label: "Planning" },
      { value: "Closed", label: "Closed" },
      { value: "Finished", label: "Finished" },
      { value: "In progress", label: "In progress" },
    ])
    loadECOptions({ centerId })
    const nextState = { ...state, centerId, page: 0 }
    setState(nextState)
    handlerAPIList(nextState)
  }, [centerId])

  const handleAcsStatusCardClick = (acsStatusValue) => {
    const isActive = acsStatusFilter?.value === acsStatusValue
    const nextOption = isActive
      ? null
      : ACS_STATUS_OPTIONS.find((o) => o.value === acsStatusValue) ?? null
    const nextState = {
      ...state,
      page: 0,
      acs_status: nextOption?.value ?? null,
    }
    setAcsStatusFilter(nextOption)
    setState(nextState)
    handlerAPIList(nextState)
  }

  const handleExportCsv = () => {
    exportClassSituationCsv({
      rows: dataTable?.rows ?? [],
      visibleColumns,
      summaryGroups,
      tableSummaryItems: buildClassSituationSummaryItems(tableSummary),
      search: state.search,
      statusFilter,
      acsStatusFilter,
      selectedEc,
      filterData,
    })
  }

  const handleAddNote = async (noteText, row) => {
    const classCode = row?.classCode
    if (!classCode || !centerId) return
    await createNote({
      centerId: String(centerId),
      classCode,
      notes: noteText,
    }).unwrap()
  }

  return (
    <MDBox mb={3} className={`class-situation class-situation-lxo`}>
      {isInitialLoading ? (
        <Loading style={{ height: 200, margin: "10% auto" }} />
      ) : (
        <Card>
          <MDBox px={2} py={2} className="class-situation-lxo-filter-section">
            <MDBox
              display="grid"
              gridTemplateColumns={{
                xs: "1fr",
                md: "minmax(240px, 2fr) repeat(3, minmax(180px, 1fr)) auto",
              }}
              gap={1.5}
              alignItems="center"
            >
              <TextField
                label="Search by class name"
                value={state.search}
                onChange={(e) => {
                  const next = e.target.value
                  setSearchInput(next)
                  setState((prev) => ({ ...prev, search: next }))
                }}
                variant="outlined"
                size="small"
                fullWidth
              />

              <MDBox sx={{ minWidth: "180px" }}>
                <MDSelect
                  options={statusOptions}
                  placeholder="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    const nextState = {
                      ...state,
                      page: 0,
                      status: e?.value ?? null,
                    }
                    setStatusFilter(e || null)
                    setState(nextState)
                    handlerAPIList(nextState)
                  }}
                />
              </MDBox>

              <MDBox sx={{ minWidth: "180px" }}>
                <MDSelect
                  options={ACS_STATUS_OPTIONS}
                  placeholder="ACS Status"
                  value={acsStatusFilter}
                  onChange={(e) => {
                    const nextState = {
                      ...state,
                      page: 0,
                      acs_status: e?.value ?? null,
                    }
                    setAcsStatusFilter(e || null)
                    setState(nextState)
                    handlerAPIList(nextState)
                  }}
                />
              </MDBox>

              <MDBox sx={{ minWidth: "180px" }}>
                <MDSelect
                  options={ecOptions}
                  placeholder="Filter by EC"
                  value={selectedEc}
                  onChange={(e) => {
                    const nextState = {
                      ...state,
                      page: 0,
                      cxo: e?.value ?? "",
                    }
                    setSelectedEc(e || null)
                    setFilterData((prev) => ({ ...prev, cxo: e || null }))
                    setState(nextState)
                    handlerAPIList(nextState)
                  }}
                />
              </MDBox>

              <MDBox
                sx={{
                  gridColumn: { xs: "1 / -1", md: "auto" },
                  justifySelf: { xs: "stretch", md: "end" },
                }}
                display="flex"
                gap={1}
              >
                <Button
                  className={`btn-top-table`}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                >
                  More Filters
                </Button>
                <Button
                  className={`btn-top-table`}
                  variant="outlined"
                  startIcon={<TableRowsOutlinedIcon />}
                  onClick={(event) => setColumnAnchorEl(event.currentTarget)}
                >
                  Columns
                </Button>
              </MDBox>
            </MDBox>
          </MDBox>

          <MDBox className="class-situation-lxo-summary-wrapper">
            <MDBox className="class-situation-lxo-summary-groups">
              {summaryGroups.map((group) => {
                const isExpanded = !!expandedCategories[group.category]
                const hasMore = group.cards.length > PREVIEW_CARDS_PER_CATEGORY
                const visibleCards = isExpanded
                  ? group.cards
                  : group.cards.slice(0, PREVIEW_CARDS_PER_CATEGORY)

                return (
                  <div
                    key={group.category}
                    className="class-situation-lxo-summary-group"
                  >
                    <div className="class-situation-lxo-summary-group__header">
                      <div className="class-situation-lxo-summary-group__title">
                        {group.category}
                      </div>
                      {hasMore && (
                        <Button
                          className="class-situation-lxo-summary-group__toggle"
                          variant="text"
                          size="small"
                          onClick={() => toggleCategory(group.category)}
                          endIcon={
                            isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )
                          }
                        >
                          {isExpanded
                            ? "Collapse"
                            : `Expand (+${
                                group.cards.length -
                                PREVIEW_CARDS_PER_CATEGORY
                              })`}
                        </Button>
                      )}
                    </div>
                    <div className="class-situation-lxo-summary-group__cards">
                      {visibleCards.map((item) => {
                        const isClickable = !!item.acsStatusValue
                        const isActive =
                          isClickable && acsStatusFilter?.value === item.acsStatusValue
                        return (
                          <div
                            key={item.title}
                            className={`class-situation-lxo-summary__card ${item.tone} ${
                              isClickable ? "is-clickable" : ""
                            } ${isActive ? "is-active" : ""}`}
                            onClick={
                              isClickable
                                ? () => handleAcsStatusCardClick(item.acsStatusValue)
                                : undefined
                            }
                          >
                            <div className="class-situation-lxo-summary__content">
                              <span className="label">{item.title}</span>
                              <span className="value">{item.value}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </MDBox>
          </MDBox>

          <FilterClassSituation
            initialFilters={filterData}
            onSubmit={handleFilterSubmit}
            anchorEl={filterAnchorEl}
            setAnchorEl={setFilterAnchorEl}
            className={`class-situation-filter`}
          />

          <ColumnTreeMenu
            anchorEl={columnAnchorEl}
            onClose={() => setColumnAnchorEl(null)}
            visible={visibleColumns}
            onChange={updateVisibleColumns}
          />

          <MDBox className="table-container">
            <MDBox className="group-table">
              <TableSituation
                className="table"
                table={{ ...dataTable, columns: filteredColumns, summary: tableSummary }}
                onAddNote={handleAddNote}
              />
            </MDBox>

            <MDBox className="table-footer">
              <MDBox className="table-footer-showing">
                Showing {dataTable?.rows?.length} entries
              </MDBox>
              <Button
                className="btn-top-table"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={!dataTable?.rows?.length}
                onClick={handleExportCsv}
              >
                Export CSV
              </Button>
            </MDBox>
          </MDBox>
        </Card>
      )}
    </MDBox>
  )
}

export default ClassSituationLXO
